import { ROLE_LABELS } from '@fos/shared/labels';
import { Check, ChevronRight, Lock, LogOut, Plus, Search, Settings, StickyNote } from 'lucide-react';
import { useCallback, useEffect, useId, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { errorMessage } from '../../lib/api';
import { type ActiveSession, useActiveSession, useSwitchWorkspace } from '../../lib/auth';
import { cn } from '../../lib/cn';
import { type NavBadges, useNavBadges } from '../../lib/nav-badges';
import { useScope } from '../../lib/scope';
import { useAccess } from '../../lib/useAccess';
import { Avatar } from '../../ui/Avatar';
import { Badge } from '../../ui/Badge';
import { Sheet } from '../../ui/Dialog';
import { LogoSymbol } from '../../ui/Logo';
import { Segmented } from '../../ui/Segmented';
import { toast } from '../../ui/Toast';
import { ADD_ACTIONS, APPROVALS_NAV, MORE_NAV, type NavItem, PRIMARY_NAV } from './nav';
import { useMyWorkspaces, useOtherWorkspaces } from './other-workspaces';
import { useShell } from './shell-context';
import { useSignOut } from './UserMenus';

/**
 * Panel „Menu” na telefonie (DECISIONS 2026-10-17 [P] „Telefon: łatwy dostęp do całego menu”): pełny ekran z „Zamknij”,
 * otwierany przyciskiem „Menu” w dolnym menu. Od góry: wyszukiwanie (otwiera paletę ⌘K — klienci, zadania, polecenia)
 * i „Dodaj”, przełącznik „Moje / Zespół” (jak w menu bocznym), Akceptacje z liczbą do decyzji, wszystkie moduły osoby
 * pogrupowane jak w menu bocznym (z ikonami i plakietkami), workspace'y z licznikami tego, co czeka w innych
 * (`useOtherWorkspaces`), konto: Ustawienia, Hasło i urządzenia, Wyloguj.
 * Klik w pozycję zamyka panel i przechodzi (zastępując wpis panelu w historii); Wstecz w przeglądarce / telefonie
 * zamyka panel (`useMobileMenuHistory`). Fokus w pułapce, Esc i „Zamknij” zamykają (Radix Dialog przez `Sheet`).
 * Pozycje ukrywane wg dostępu z sesji — tylko UI, backend i tak sprawdza dostęp.
 */
export function MobileMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      side="bottom"
      title="Menu"
      className="top-0 h-dvh max-h-dvh rounded-none pt-[env(safe-area-inset-top)]"
    >
      <MobileMenuBody onClose={() => onOpenChange(false)} />
    </Sheet>
  );
}

function MobileMenuBody({ onClose }: { onClose: () => void }) {
  const session = useActiveSession();
  const access = useAccess();
  const badges = useNavBadges();
  const { openCommandPalette } = useShell();
  const { scope, setScope } = useScope();

  const visible = (item: NavItem) => item.module === null || access.can(item.module);
  const main = PRIMARY_NAV.filter((item) => item.key !== APPROVALS_NAV.key && visible(item));
  const more = MORE_NAV.filter(visible);
  const canAdd = ADD_ACTIONS.some((a) => access.can(a.module, 'edit'));
  const approvals = visible(APPROVALS_NAV) ? APPROVALS_NAV : null;

  // paleta ⌘K otwiera się nad stroną, nie nad panelem (panel się zamyka)
  const palette = (mode: 'all' | 'add') => {
    onClose();
    openCommandPalette(mode);
  };

  return (
    <div className="flex flex-col gap-6 pb-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => palette('all')}
          className={cn(
            'flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg border border-line bg-surface px-3 text-left',
            'text-body-s text-fg-muted transition-colors active:bg-elevated',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <Search size={18} strokeWidth={1.75} aria-hidden className="shrink-0" />
          <span className="truncate">Szukaj klientów, zadań, poleceń</span>
        </button>
        {canAdd && (
          <button
            type="button"
            onClick={() => palette('add')}
            aria-label="Dodaj"
            className={cn(
              'inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-elevated text-fg transition-colors',
              'active:bg-line focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            <Plus size={20} strokeWidth={1.75} aria-hidden />
          </button>
        )}
        {/* 17.10.2026: „Zanotuj” — pole szybkiej notatki (karteczki) na Dziś, od razu z fokusem */}
        <NavLink
          to="/dzis#zanotuj"
          replace
          aria-label="Zanotuj"
          title="Zanotuj"
          className={cn(
            'inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-elevated text-fg transition-colors',
            'active:bg-line focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <StickyNote size={20} strokeWidth={1.75} aria-hidden />
        </NavLink>
      </div>

      {access.isManagerOrAbove && (
        <Segmented
          fullWidth
          // pole dotyku 44 px (40 + obramowanie toru)
          className="[&>*]:h-10"
          aria-label="Zakres danych"
          value={scope}
          onValueChange={setScope}
          options={[
            { value: 'mine', label: 'Moje' },
            { value: 'team', label: 'Zespół' },
          ]}
        />
      )}

      <nav aria-label="Moduły" className="flex flex-col gap-2">
        {approvals && <MenuTile item={approvals} badges={badges} wide />}
        {main.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {main.map((item) => (
              <MenuTile key={item.key} item={item} badges={badges} />
            ))}
          </div>
        )}
        {more.length > 0 && (
          <>
            <h2 className="pt-3 pb-0.5 pl-1 text-caption font-medium text-fg-muted">Więcej</h2>
            <div className="grid grid-cols-2 gap-2">
              {more.map((item) => (
                <MenuTile key={item.key} item={item} badges={badges} />
              ))}
            </div>
          </>
        )}
      </nav>

      <WorkspaceSection session={session} />
      <AccountSection session={session} />
    </div>
  );
}

const tileBase = cn(
  'flex min-h-12 items-center gap-3 rounded-xl px-3 py-2 text-body-s transition-colors',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
);

function MenuTile({ item, badges, wide = false }: { item: NavItem; badges: NavBadges; wide?: boolean }) {
  const Icon = item.icon;
  const badge = item.module ? badges[item.module] : undefined;
  const count = badge?.count ?? 0;
  const emphasized = count > 0 && badge?.kind !== 'muted';
  return (
    <NavLink
      to={item.to}
      // panel jest wpisem w historii: przejście go zastępuje, więc Wstecz wraca na stronę sprzed otwarcia menu
      replace
      className={({ isActive }) =>
        cn(
          tileBase,
          isActive ? 'bg-elevated font-semibold text-fg' : 'bg-surface font-medium text-fg active:bg-elevated',
          wide && emphasized && !isActive && 'bg-accent-subtle',
        )
      }
    >
      <Icon
        size={20}
        strokeWidth={1.75}
        aria-hidden
        className={cn('shrink-0', emphasized ? 'text-fg' : 'text-fg-secondary')}
      />
      <span className={cn('min-w-0 flex-1 truncate', emphasized && 'font-semibold')}>
        {item.label}
        {count > 0 && (
          <span className="sr-only">
            {badge?.kind === 'attention' ? ', do zrobienia: ' : badge?.kind === 'muted' ? ', do odbioru: ' : ', nowe: '}
            {count}
          </span>
        )}
      </span>
      {badge && count > 0 && <Badge kind={badge.kind} count={count} />}
      {wide && <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />}
    </NavLink>
  );
}

function WorkspaceSection({ session }: { session: ActiveSession }) {
  const workspaces = useMyWorkspaces();
  // 8.10: liczba rzeczy czekających w innych workspace'ach (te same dane co plakietka w menu bocznym i sekcja na Dziś)
  const waiting = new Map(useOtherWorkspaces().data.workspaces.map((w) => [w.workspace.id, w.waiting]));
  const switchWorkspace = useSwitchWorkspace();
  const navigate = useNavigate();
  const headingId = useId();

  const items =
    workspaces.data ?? (session.workspace ? [{ ...session.workspace, role: session.membership?.role ?? 'user' }] : []);
  const current = session.workspace?.id ?? '';
  if (items.length === 0) return null;

  const choose = (workspaceId: string) => {
    if (workspaceId === current || switchWorkspace.isPending) return;
    switchWorkspace.mutateAsync(workspaceId).then(
      () => navigate('/dzis', { replace: true }),
      (err: unknown) => toast.error(errorMessage(err)),
    );
  };

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1.5">
      <h2 id={headingId} className="pl-1 text-caption font-medium text-fg-muted">
        Workspace
      </h2>
      <ul className="flex flex-col overflow-hidden rounded-xl border border-line">
        {items.map((ws) => {
          const selected = ws.id === current;
          const n = waiting.get(ws.id) ?? 0;
          const role = 'partner' in ws && ws.partner ? 'Partner' : ROLE_LABELS[ws.role];
          const content = (
            <>
              <LogoSymbol size={20} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className={cn('truncate text-body-s text-fg', selected && 'font-semibold')}>{ws.name}</span>
                <span className="truncate text-caption text-fg-muted">
                  {role}
                  {'primary' in ws && ws.primary ? ' · główny' : ''}
                </span>
              </span>
              {n > 0 && (
                <>
                  <Badge kind="attention" count={n} />
                  <span className="sr-only">{`, czeka: ${n}`}</span>
                </>
              )}
              {selected && items.length > 1 && (
                <Check size={18} strokeWidth={2} aria-hidden className="shrink-0 text-accent" />
              )}
            </>
          );
          const row = 'flex min-h-14 items-center gap-3 border-b border-line px-3 py-2 last:border-b-0';
          if (items.length === 1) {
            return (
              <li key={ws.id} className={row}>
                {content}
              </li>
            );
          }
          return (
            <li key={ws.id} className="border-b border-line last:border-b-0">
              <button
                type="button"
                aria-current={selected ? 'true' : undefined}
                onClick={() => choose(ws.id)}
                className={cn(
                  row,
                  'w-full border-b-0 text-left transition-colors active:bg-elevated',
                  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                  selected ? 'bg-surface' : 'bg-canvas',
                )}
              >
                {content}
                {!selected && <span className="sr-only">{', przełącz'}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function AccountSection({ session }: { session: ActiveSession }) {
  const signOut = useSignOut();
  const headingId = useId();
  const role = session.membership?.role;
  const row = cn(
    'flex min-h-12 w-full items-center gap-3 border-b border-line px-3 py-2 text-left text-body-s text-fg last:border-b-0',
    'transition-colors active:bg-elevated focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
  );
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1.5">
      <h2 id={headingId} className="sr-only">
        Konto
      </h2>
      <div className="flex items-center gap-3 px-1 py-1">
        <Avatar initials={session.user.initials} color={session.user.avatarColor} size={40} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-body-s font-medium text-fg">{session.user.name}</span>
          <span className="truncate text-caption text-fg-muted">
            {session.user.email}
            {role ? ` · ${ROLE_LABELS[role]}` : ''}
          </span>
        </span>
      </div>
      <div className="flex flex-col overflow-hidden rounded-xl border border-line">
        <NavLink to="/ustawienia/profil" replace className={row}>
          <Settings size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-secondary" />
          <span className="flex-1">Ustawienia</span>
          <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
        </NavLink>
        <NavLink to="/ustawienia/bezpieczenstwo" replace className={row}>
          <Lock size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-secondary" />
          <span className="flex-1">Hasło i urządzenia</span>
          <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
        </NavLink>
        <button type="button" onClick={signOut} className={row}>
          <LogOut size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-secondary" />
          <span className="flex-1">Wyloguj</span>
        </button>
      </div>
    </section>
  );
}

/** Znacznik panelu Menu w `history.state` (stan lokalizacji routera). */
export const MOBILE_MENU_STATE_KEY = 'fosMobileMenu';

function hasMenuFlag(state: unknown): boolean {
  return (
    typeof state === 'object' && state !== null && (state as Record<string, unknown>)[MOBILE_MENU_STATE_KEY] === true
  );
}

function withoutMenuFlag(state: unknown): unknown {
  if (typeof state !== 'object' || state === null) return state ?? null;
  const { [MOBILE_MENU_STATE_KEY]: _flag, ...rest } = state as Record<string, unknown>;
  return Object.keys(rest).length > 0 ? rest : null;
}

/**
 * Panel Menu jako wpis w historii (ta sama ścieżka, znacznik w stanie lokalizacji): otwarcie dodaje wpis, więc Wstecz
 * w przeglądarce albo gest „wstecz” telefonu zamyka panel zamiast opuszczać stronę. Zamknięcie „Zamknij” / Esc cofa
 * ten wpis; przejście z panelu go zastępuje (`replace` w pozycjach). Po przeładowaniu strony z panelem w historii
 * panel się nie otwiera sam (znacznik jest czyszczony).
 */
export function useMobileMenuHistory(): { open: boolean; openMenu: () => void; closeMenu: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const open = hasMenuFlag(location.state);
  // wpis panelu dodany w tej sesji strony — tylko wtedy zamknięcie cofa historię
  const pushed = useRef(false);
  const here = `${location.pathname}${location.search}${location.hash}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy montowaniu (przeładowanie z panelem w historii)
  useEffect(() => {
    if (open && !pushed.current) navigate(here, { replace: true, state: withoutMenuFlag(location.state) });
  }, []);

  useEffect(() => {
    if (!open) pushed.current = false;
  }, [open]);

  const openMenu = useCallback(() => {
    if (open) return;
    pushed.current = true;
    const state = typeof location.state === 'object' && location.state !== null ? location.state : {};
    navigate(here, { state: { ...state, [MOBILE_MENU_STATE_KEY]: true } });
  }, [open, here, location.state, navigate]);

  const closeMenu = useCallback(() => {
    if (!open) return;
    if (pushed.current) {
      pushed.current = false;
      navigate(-1);
    } else {
      navigate(here, { replace: true, state: withoutMenuFlag(location.state) });
    }
  }, [open, here, location.state, navigate]);

  return { open, openMenu, closeMenu };
}
